import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { apiClient, createApiClient } from "../../api/apiClient";
import { handleNHOErrorWithFriendlyMessage, NHO_API_NAMES } from '../utils/nhoErrorHandler';

// Define interface for training content structure from the API
export interface TrainingContent {
  Title: string; // API returns Title
  MessageText: string; // API returns MessageText
  Order: number; // API returns Order
  // Keep old fields for backward compatibility
  id?: string;
  title?: string;
  content?: string;
}

// Define interface for undertaking response item
export interface UndertakingItem {
  Id: number;
  Emp_Name: string;
  GPID: string;
  Week: number;
  Period: number;
  Training_type: number;
  Employee_Sign: string | null;
  Observation_Notes: string | null;
  Mng_Sign: string | null;
  Mng_Sign_Status: number;
  Emp_Sign_Status: number;
  Pepsi_Week: number;
  Mng_Name: string | null;
  Created: string | null;
}

export interface EmployeeProfileState {
  trainingContents: TrainingContent[];
  isLoading: boolean;
  error: string | null;
  isDataFetched: boolean; // Flag to track if data has been fetched this session
  undertakings: {
    data: UndertakingItem[];
    isLoading: boolean;
    error: string | null;
  };
}

const initialState: EmployeeProfileState = {
  trainingContents: [],
  isLoading: false,
  error: null,
  isDataFetched: false,
  undertakings: {
    data: [],
    isLoading: false,
    error: null,
  },
};

// Async thunk for fetching training content
export const fetchTrainingContents = createAsyncThunk(
  "employeeProfile/fetchTrainingContents",
  async (employeeId: string, { rejectWithValue }) => {
    try {
      const api = createApiClient();
      const response = await apiClient(
        api,
        `/new-hire-onboarding/static_undertaking${
          employeeId ? `?employeeId=${employeeId}` : ""
        }`,
        "GET"
      );

      // API returns data in a nested 'data' property
      if (response && response.data) {
        console.log("API response structure:", Object.keys(response));
        return response.data;
      } else {
        console.log("Unexpected API response structure:", response);
        return [];
      }
    } catch (error: any) {
      console.error("API error:", error);
      try {
        handleNHOErrorWithFriendlyMessage(NHO_API_NAMES.FETCH_STATIC_UNDERTAKING, error, true, { employeeId });
      } catch (e) {
        console.error('Error while dispatching NHO error handler for static_undertaking:', e);
      }
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch training contents"
      );
    }
  }
);

// Async thunk for fetching undertaking details by employee ID
export const fetchUndertakingDetails = createAsyncThunk(
  "employeeProfile/fetchUndertakingDetails",
  async (_, { rejectWithValue, getState }) => {
    try {
      // Get the gpid from employeeUpdatesSlice
      const state = getState() as { employeeUpdates: { gpid: string } };
      const gpid = state.employeeUpdates.gpid;

      //console.log("Full Redux state:", JSON.stringify(getState()));
      //console.log("employeeUpdates slice:", JSON.stringify(state.employeeUpdates));

      // Validate gpid before proceeding
      if (!gpid) {
        throw new Error(
          "GPID is required and not available in employeeUpdatesSlice"
        );
      }

      console.log("Fetching undertaking details with GPID:", gpid);
      console.log("GPID type:", typeof gpid, "GPID length:", gpid.length);

      // Convert GPID to integer before using it
      const gpidAsInt = parseInt(gpid, 10);

      // Check if conversion was successful
      if (isNaN(gpidAsInt)) {
        throw new Error("GPID could not be converted to an integer");
      }

      console.log("Converted GPID to integer:", gpidAsInt);

      // Create API client
      const api = createApiClient();

      // For GET requests, parameters should be in the URL query string using the integer GPID
      const url = `/new-hire-onboarding/undertaking?GPID=${gpidAsInt}`;

      console.log("Making API call to:", url);

      // Call the API with ID in query params
      // Let's also add some headers for diagnostics
      api.defaults.headers.get["Content-Type"] = "application/json";
      api.defaults.headers.get.Accept = "application/json";

      console.log("API headers:", JSON.stringify(api.defaults.headers));

      const response = await apiClient(api, url, "GET");

      console.log("Undertaking API response:", response);

      // Validate the response
      if (!response) {
        throw new Error("Empty response received from undertaking API");
      }

      // Check if the response has a data property (common API pattern)
      if (response.data !== undefined) {
        return response.data;
      }

      return response;
    } catch (error: any) {
      console.error("Undertaking API error:", error);
      try {
        handleNHOErrorWithFriendlyMessage(NHO_API_NAMES.FETCH_UNDERTAKING || 'fetchUndertakingDetails', error, true, {});
      } catch (e) {
        console.error('Error while dispatching NHO error handler:', e);
      }
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch undertaking details"
      );
    }
  }
);

const employeeProfileSlice = createSlice({
  name: "employeeProfile",
  initialState,
  reducers: {
    resetTrainingContents: (state) => {
      state.trainingContents = [];
      state.error = null;
    },
    resetFetchFlag: (state) => {
      state.isDataFetched = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTrainingContents.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        fetchTrainingContents.fulfilled,
        (state, action: PayloadAction<TrainingContent[]>) => {
          state.isLoading = false;
          state.trainingContents = action.payload;
          state.isDataFetched = true;
        }
      )
      .addCase(fetchTrainingContents.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchUndertakingDetails.pending, (state) => {
        state.undertakings.isLoading = true;
        state.undertakings.error = null;
      })
      .addCase(
        fetchUndertakingDetails.fulfilled,
        (state, action: PayloadAction<UndertakingItem[]>) => {
          state.undertakings.isLoading = false;
          state.undertakings.data = action.payload;
        }
      )
      .addCase(fetchUndertakingDetails.rejected, (state, action) => {
        state.undertakings.isLoading = false;
        state.undertakings.error = action.payload as string;
      });
  },
});

export const { resetTrainingContents, resetFetchFlag } =
  employeeProfileSlice.actions;
export default employeeProfileSlice.reducer;
